import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { useMode } from '../contexts/ModeContext';
import { useLang } from '../contexts/LangContext';
import { authAPI, hafizlikAPI } from '../utils/api';
import {
  ArrowRight, BookOpen, GraduationCap, Award, Search,
  Layers, ChevronRight, CheckCircle2, Circle, RotateCw,
} from 'lucide-react';
import { toast } from 'sonner';

const CIG_DEFAULT = 3;
const CIG_KEY = 'hafizim_cig_count';
const MODE_KEY = 'hafizim_hifz_mode';

const Hafizlik = () => {
  const { isKidsMode } = useMode();
  const { lang } = useLang();
  const navigate = useNavigate();
  const accent = isKidsMode ? 'amber' : 'teal';

  const [user, setUser] = useState(null);
  const [juzMeta, setJuzMeta] = useState([]);
  const [pageProgress, setPageProgress] = useState({}); // {page: {status}}
  const [lastPos, setLastPos] = useState(null);

  const [hifzMode, setHifzMode] = useState(() => {
    try { return localStorage.getItem(MODE_KEY) || 'learning'; } catch { return 'learning'; }
  });
  const [cigCount, setCigCount] = useState(() => {
    try { return parseInt(localStorage.getItem(CIG_KEY) || `${CIG_DEFAULT}`, 10); } catch { return CIG_DEFAULT; }
  });
  const [selectedJuz, setSelectedJuz] = useState(1);
  const [pageQuery, setPageQuery] = useState('');

  useEffect(() => { try { localStorage.setItem(MODE_KEY, hifzMode); } catch (_) { /* ignore */ } }, [hifzMode]);
  useEffect(() => { try { localStorage.setItem(CIG_KEY, String(cigCount)); } catch (_) { /* ignore */ } }, [cigCount]);

  useEffect(() => {
    (async () => {
      try {
        const [u, jm, pp, lp] = await Promise.all([
          authAPI.getMe(),
          hafizlikAPI.juzMeta(),
          hafizlikAPI.listPageProgress(),
          hafizlikAPI.getLastPosition(),
        ]);
        setUser(u.data);
        setJuzMeta(jm.data?.juz || []);
        const map = {};
        for (const p of (pp.data?.pages || [])) map[p.page] = p;
        setPageProgress(map);
        setLastPos(lp.data || null);
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  const memorizedCount = useMemo(
    () => Object.values(pageProgress).filter((p) => p.status === 'memorized').length,
    [pageProgress]
  );
  const inProgressCount = useMemo(
    () => Object.values(pageProgress).filter((p) => p.status === 'in_progress').length,
    [pageProgress]
  );

  // Pages for selected juz
  const pagesForJuz = useMemo(() => {
    const meta = juzMeta.find((j) => j.juz === selectedJuz);
    if (!meta) return [];
    const arr = [];
    for (let p = meta.start_page; p <= meta.end_page; p++) arr.push(p);
    return arr;
  }, [juzMeta, selectedJuz]);

  // Çiğ: last N pages of selected juz, reversed for memorization order
  const cigList = useMemo(() => {
    if (!pagesForJuz.length) return [];
    return pagesForJuz.slice(-cigCount).reverse();
  }, [pagesForJuz, cigCount]);

  const openPage = (page) => navigate(`/hafizlik/sayfa/${page}?mode=${hifzMode}`);

  const handleSearchPage = () => {
    const n = parseInt(pageQuery, 10);
    if (!Number.isFinite(n) || n < 1 || n > 604) {
      toast.error(lang === 'tr' ? 'Sayfa 1 ile 604 arasında olmalı' : 'Page must be 1-604');
      return;
    }
    openPage(n);
  };

  const tone = isKidsMode
    ? { brand: 'amber', primaryBg: 'bg-amber-500 hover:bg-amber-600', softBg: 'bg-amber-50', softText: 'text-amber-800', ring: 'ring-amber-400', border: 'border-amber-200' }
    : { brand: 'teal', primaryBg: 'bg-teal-700 hover:bg-teal-800', softBg: 'bg-teal-50', softText: 'text-teal-800', ring: 'ring-teal-400', border: 'border-teal-200' };

  return (
    <AppShell user={user} title={lang === 'tr' ? 'Hafızlığa Devam' : 'Hifz'}>
      <div className="max-w-6xl mx-auto p-4 sm:p-8 md:p-12 space-y-8">
        {/* Header */}
        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">{lang === 'tr' ? 'Hafızlık' : 'Hifz'}</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {lang === 'tr' ? 'Sayfa Sayfa Ezber' : 'Page-by-Page Memorization'}
            </h1>
            <p className="text-stone-600 mt-2 max-w-2xl">
              {lang === 'tr'
                ? 'Cüz seç, sayfanı bul, Çiğ usulüyle veya istediğin tempoda ezberle. Hafızlık moduna geçtiğinde mikrofonla okuduğunu sistem kontrol eder.'
                : 'Pick a juz, find your page, memorize using Çiğ method or your own pace.'}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <StatChip label={lang === 'tr' ? 'Ezberlenen' : 'Memorized'} value={`${memorizedCount}/604`} icon={<Award className="w-4 h-4" />} tone={tone} />
            <StatChip label={lang === 'tr' ? 'Devam Eden' : 'In Progress'} value={inProgressCount} icon={<RotateCw className="w-4 h-4" />} tone={tone} />
          </div>
        </header>

        {/* Mode tabs */}
        <div className="bg-white rounded-2xl border border-stone-200 p-2 inline-flex w-full sm:w-auto">
          <ModeTab
            active={hifzMode === 'learning'}
            onClick={() => setHifzMode('learning')}
            icon={<GraduationCap className="w-4 h-4" />}
            title={lang === 'tr' ? 'Hafızlık Yapan' : 'Memorizing'}
            subtitle={lang === 'tr' ? 'Öğrenme & tekrar' : 'Learn & repeat'}
            tone={tone}
            data-testid="hifz-mode-learning"
          />
          <ModeTab
            active={hifzMode === 'reviewing'}
            onClick={() => setHifzMode('reviewing')}
            icon={<BookOpen className="w-4 h-4" />}
            title={lang === 'tr' ? 'Hafız' : 'Hafiz'}
            subtitle={lang === 'tr' ? 'Mikrofonla ezber ver' : 'Recite with mic'}
            tone={tone}
            data-testid="hifz-mode-reviewing"
          />
        </div>

        {/* Resume + Quick page search */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {lastPos?.page ? (
            <button
              type="button"
              onClick={() => openPage(lastPos.page)}
              data-testid="hifz-resume-btn"
              className={`text-left bg-white rounded-2xl p-5 border ${tone.border} hover:shadow-md transition-all flex items-center gap-4`}
            >
              <div className={`w-12 h-12 rounded-xl ${tone.softBg} ${tone.softText} flex items-center justify-center`}>
                <RotateCw className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wider text-stone-500">{lang === 'tr' ? 'Kaldığın yer' : 'Resume'}</p>
                <p className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {lang === 'tr' ? 'Sayfa' : 'Page'} {lastPos.page}
                </p>
                <p className="text-xs text-stone-500">{lang === 'tr' ? 'Devam et' : 'Continue'} <ChevronRight className="inline w-3 h-3" /></p>
              </div>
            </button>
          ) : (
            <div className="bg-stone-50 rounded-2xl p-5 border border-dashed border-stone-200 text-stone-500 text-sm flex items-center gap-3">
              <BookOpen className="w-5 h-5" />
              {lang === 'tr' ? 'Henüz çalışma yapmadın. Aşağıdan bir sayfa seç.' : 'No session yet. Pick a page below.'}
            </div>
          )}

          <div className="bg-white rounded-2xl p-5 border border-stone-200 flex items-center gap-3">
            <Search className="w-5 h-5 text-stone-400" />
            <input
              type="number"
              min="1"
              max="604"
              value={pageQuery}
              onChange={(e) => setPageQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearchPage(); }}
              placeholder={lang === 'tr' ? 'Sayfa numarası (1-604)' : 'Page number (1-604)'}
              className="flex-1 bg-transparent outline-none text-sm"
              data-testid="hifz-page-search-input"
            />
            <button
              type="button"
              onClick={handleSearchPage}
              data-testid="hifz-page-search-btn"
              className={`px-4 py-2 rounded-xl text-white text-sm font-semibold ${tone.primaryBg}`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {lang === 'tr' ? 'Aç' : 'Go'}
            </button>
          </div>
        </div>

        {/* Çiğ panel */}
        <section className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {lang === 'tr' ? 'Çiğ Sistemi' : 'Çiğ System'}
              </h2>
              <p className="text-sm text-stone-600 mt-1">
                {lang === 'tr'
                  ? `Her cüzün son ${cigCount} sayfasını sondan başa ezberle (örn. 20→19→18), okurken baştan sona oku.`
                  : `Memorize the last ${cigCount} pages of each juz from end to start, recite forward.`}
              </p>
            </div>
            <div className="flex items-center gap-2 bg-white rounded-xl border border-stone-200 p-1">
              <span className="text-xs text-stone-500 px-2">{lang === 'tr' ? 'Sayfa adedi' : 'Pages'}</span>
              {[2, 3, 5, 10].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setCigCount(n)}
                  data-testid={`cig-count-${n}`}
                  className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                    cigCount === n
                      ? `${tone.primaryBg} text-white`
                      : 'text-stone-600 hover:bg-stone-100'
                  }`}
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >{n}</button>
              ))}
            </div>
          </div>

          {/* Juz selector pills */}
          <div className="flex flex-wrap gap-2">
            {juzMeta.map((j) => (
              <button
                key={j.juz}
                type="button"
                onClick={() => setSelectedJuz(j.juz)}
                data-testid={`juz-pill-${j.juz}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                  selectedJuz === j.juz
                    ? `${tone.primaryBg} text-white border-transparent`
                    : 'bg-white text-stone-700 border-stone-200 hover:border-stone-300'
                }`}
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {lang === 'tr' ? `Cüz ${j.juz}` : `Juz ${j.juz}`}
              </button>
            ))}
          </div>

          {/* Çiğ pages for selected juz */}
          <div className="bg-white rounded-2xl border border-stone-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Layers className={`w-4 h-4 ${tone.softText}`} />
              <p className="text-sm font-semibold text-stone-700">
                {lang === 'tr' ? `Cüz ${selectedJuz} — Ezber sırası (sondan)` : `Juz ${selectedJuz} — Memorize order (reversed)`}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              {cigList.map((p, idx) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => openPage(p)}
                  data-testid={`cig-page-${p}`}
                  className={`group relative px-4 py-3 rounded-xl border-2 transition-all ${
                    pageProgress[p]?.status === 'memorized'
                      ? `${tone.softBg} ${tone.border} ${tone.softText}`
                      : 'bg-stone-50 border-stone-200 text-stone-700 hover:border-stone-300'
                  }`}
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  <span className="block text-[10px] uppercase tracking-wider opacity-70">
                    {idx + 1}.
                  </span>
                  <span className="block text-lg font-bold leading-tight">
                    {lang === 'tr' ? `s.${p}` : `p.${p}`}
                  </span>
                  {pageProgress[p]?.status === 'memorized' && (
                    <CheckCircle2 className="absolute -top-2 -right-2 w-5 h-5 text-emerald-600 bg-white rounded-full" />
                  )}
                </button>
              ))}
            </div>
            <p className="text-xs text-stone-500">
              {lang === 'tr'
                ? `Okuma (ezber verme) sırası: ${[...cigList].reverse().map(p => `s.${p}`).join(' → ')}`
                : `Recitation order: ${[...cigList].reverse().map(p => `p.${p}`).join(' → ')}`}
            </p>
          </div>

          {/* All pages of this juz */}
          <div>
            <p className="text-sm font-semibold text-stone-700 mb-2">
              {lang === 'tr' ? `Cüz ${selectedJuz} — Tüm sayfalar` : `Juz ${selectedJuz} — All pages`}
            </p>
            <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2">
              {pagesForJuz.map((p) => {
                const status = pageProgress[p]?.status;
                const isMem = status === 'memorized';
                const isProg = status === 'in_progress';
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => openPage(p)}
                    data-testid={`juz-page-${p}`}
                    className={`relative aspect-square rounded-lg border-2 text-sm font-bold transition-all flex items-center justify-center ${
                      isMem
                        ? `${tone.softBg} ${tone.border} ${tone.softText}`
                        : isProg
                          ? 'bg-yellow-50 border-yellow-200 text-yellow-900'
                          : 'bg-white border-stone-200 text-stone-700 hover:border-stone-300'
                    }`}
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {p}
                    {isMem && <CheckCircle2 className="absolute -top-1.5 -right-1.5 w-4 h-4 text-emerald-600 bg-white rounded-full" />}
                    {isProg && <Circle className="absolute -top-1.5 -right-1.5 w-4 h-4 text-yellow-600 bg-white rounded-full fill-yellow-400" />}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Classic practice link */}
        <section className="bg-stone-50 rounded-2xl p-5 border border-stone-200 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-white border border-stone-200 flex items-center justify-center text-stone-600">
            <BookOpen className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-stone-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {lang === 'tr' ? 'Sure bazlı klasik ezber' : 'Surah-based classic practice'}
            </p>
            <p className="text-sm text-stone-500">
              {lang === 'tr' ? 'Sure seç, mikrofonla ayet ayet ezber kontrolü.' : 'Pick a surah, mic-by-mic ayah practice.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/practice')}
            data-testid="hifz-classic-practice"
            className={`px-4 py-2 rounded-xl text-white text-sm font-semibold ${tone.primaryBg} inline-flex items-center gap-2`}
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {lang === 'tr' ? 'Aç' : 'Open'} <ArrowRight className="w-4 h-4" />
          </button>
        </section>
      </div>
    </AppShell>
  );
};

const StatChip = ({ label, value, icon, tone }) => (
  <div className={`bg-white rounded-xl border ${tone.border} px-4 py-3`}>
    <div className="flex items-center gap-2 text-xs text-stone-500">{icon}{label}</div>
    <div className="text-xl font-bold text-stone-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</div>
  </div>
);

const ModeTab = ({ active, onClick, icon, title, subtitle, tone, ...rest }) => (
  <button
    type="button"
    onClick={onClick}
    {...rest}
    className={`flex-1 px-4 py-3 rounded-xl text-left transition-all ${
      active ? `${tone.primaryBg} text-white` : 'text-stone-700 hover:bg-stone-100'
    }`}
  >
    <div className="flex items-center gap-2">
      {icon}
      <div>
        <div className="text-sm font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</div>
        <div className={`text-[11px] ${active ? 'text-white/80' : 'text-stone-500'}`}>{subtitle}</div>
      </div>
    </div>
  </button>
);

export default Hafizlik;
